<template>
    <div
        class="well-cards"
        :class="{
            'sticky-warning': !isFdaFriendly && isTooEarlyForScores,
            'side-panel-open': cycleDetailsAreVisible && !isFdaFriendly
        }"
    >
        <demo-disclaimer v-if="showDisclaimer" />
        <inference-availability-message v-if="!isFdaFriendly && isTooEarlyForScores" />

        <wells-sub-header />

        <div class="well-cards-wrapper">
            <div class="well-cards-inner-container">
                <treatment-slide-selector />

                <div v-if="selectedSlide" class="slide-tab">
                    <well-card
                        v-for="well in selectedSlide.wells"
                        :key="well && well._id"
                        :master="well && masterWell && well._id === masterWell._id"
                        :well="well"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import KeyboardService from '../../business/services/KeyboardService'
import WellsSubHeader from './WellsSubHeader'
import WellCard from './WellCard'
import TreatmentSlideSelector from './TreatmentSlideSelector'
import InferenceAvailabilityMessage from './common/InferenceAvailabilityMessage'
import DemoDisclaimer from './common/DemoDisclaimer'

export default {
    name: 'WellCards',
    components: {
        InferenceAvailabilityMessage,
        TreatmentSlideSelector,
        WellCard,
        WellsSubHeader,
        DemoDisclaimer
    },
    computed: {
        ...mapState({
            selectedSlide: state => state.timelapse.selectedSlide,
            videoPlaybackReady: state => state.timelapse.videoPlaybackReady,
            cycleDetailsAreVisible: state => state.filter.cycleDetailsAreVisible
        }),
        ...mapGetters({
            isCycleDetailsEnabled: 'common/isCycleDetailsEnabled',
            isFdaFriendly: 'common/isFdaFriendly',
            isTooEarlyForScores: 'timelapse/isTooEarlyForScores',
            masterWell: 'timelapse/getMasterWell'
        }),
        showDisclaimer() {
            return this.showDemoDisclaimer
        }
    },
    mounted() {
        KeyboardService.subscribeTo(
            'wellCards',
            'Space',
            () => {
                console.log('toggle play, Space')
                if (this.videoPlaybackReady) {
                    this.$store.dispatch('video/togglePlayStop')
                }
            },
            true
        )
        KeyboardService.subscribeTo(
            'wellCards',
            'ArrowLeft',
            () => {
                if (this.videoPlaybackReady) {
                    this.$store.dispatch('video/skipFrame_forwardBackward', { isForward: false })
                }
            },
            true
        )
        KeyboardService.subscribeTo(
            'wellCards',
            'ArrowRight',
            () => {
                if (this.videoPlaybackReady) {
                    this.$store.dispatch('video/skipFrame_forwardBackward', { isForward: true })
                }
            },
            true
        )
        KeyboardService.subscribeTo(
            'wellCards',
            'KeyA',
            () => {
                console.log('back, KeyA')
                if (this.videoPlaybackReady) {
                    this.$store.dispatch('video/skipFrame_forwardBackward', { isForward: false })
                }
            },
            true
        )
        KeyboardService.subscribeTo(
            'wellCards',
            'KeyD',
            () => {
                console.log('fwd, KeyD')
                if (this.videoPlaybackReady) {
                    this.$store.dispatch('video/skipFrame_forwardBackward', { isForward: true })
                }
            },
            true
        )
        this.$store.commit('video/isFertilizationCheck', false)
    },
    beforeDestroy() {
        KeyboardService.unsubscribeAll('wellCards', 'Space')
        KeyboardService.unsubscribeAll('wellCards', 'ArrowLeft')
        KeyboardService.unsubscribeAll('wellCards', 'ArrowRight')
        KeyboardService.unsubscribeAll('wellCards', 'KeyA')
        KeyboardService.unsubscribeAll('wellCards', 'KeyD')
    }
}
</script>

<style lang="scss" scoped>
.well-cards {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    transition: 0.5s;
    width: 100%;

    &.side-panel-open {
        transition: 1s;
        width: calc(100% - 22rem); // side-panel width

        @media screen and #{$new-break4close} {
            width: 100%;
        }
    }
    .wells-sub-header,
    .well-cards-inner-container {
        max-width: 1344px;
        margin: 0 auto;
    }

    .well-cards-wrapper {
        display: flex;
        overflow-y: auto;
        @include hide-scrollbar();

        .well-cards-inner-container {
            display: flex;
            flex-direction: column;
            width: 100%;
        }

        .slide-tab {
            width: 100%;
            height: fit-content;
            display: grid;
            overflow: auto;
            column-gap: 1rem;
            row-gap: 1rem;
            justify-content: space-around;
            @include hide-scrollbar();

            @media screen and #{$new-break1}, #{$new-break2} {
                grid-template-columns: 1fr;
                row-gap: 0.5rem;
            }
            @media screen and #{$new-break3} {
                grid-template-columns: repeat(2, 1fr);
                row-gap: 1rem;
            }
            @media screen and #{$new-break4} {
                grid-template-columns: repeat(3, 1fr);
                row-gap: 1rem;
            }
            @media screen and #{$new-break5open} {
                grid-template-columns: repeat(4, 1fr);
            }
        }
    }

    &:not(.sticky-warning) {
        .well-cards-wrapper {
            // tablet has videos and needs space for video-controls
            @media screen and #{$new-break3} {
                height: calc(
                    100% - #{$wells-sub-header-height} - #{$well-video-controls-bottom-panel-height}
                );
            }
            @media screen and #{$new-break2close}, #{$new-break4open} {
                height: calc(100% - #{$wells-sub-header-height});
            }
        }
    }
    // header + warning doubles subheader height
    &.sticky-warning {
        .well-cards-wrapper {
            @media screen and #{$new-break3} {
                height: calc(
                    100% - #{$wells-sub-header-height} * 2 - #{$well-video-controls-bottom-panel-height}
                );
            }
            @media screen and #{$new-break2close}, #{$new-break4open} {
                height: calc(100% - #{$wells-sub-header-height} * 2);
            }
        }
    }
}
</style>
